import * as THREE from "three";
import { InstanceBatch } from "./instance_batch.js";
import { Vfx3DGlobalAtlas } from "../textures/vfx_3d_global_atlas.js";
import { ResourceID } from "../../../resource_loader/resource_id.js";
import { AttributeSize } from "../types/core_types.js";

export const LinearStreakLayout = {
  aPosStartTime:  AttributeSize.Vec4, // xyz: start_pos, w: start_time
  aVelDur:        AttributeSize.Vec4, // xyz: velocity, w: duration
  aSizeStretch:   AttributeSize.Vec4, // x: width, y: height, z: stretch_factor, w: shrink_start
  aColor:         AttributeSize.Vec3, // RGB
  aUvBounds:      AttributeSize.Vec4, // min_x, min_y, max_x, max_y
} as const;

export class VfxLinearStreakBatch extends InstanceBatch<typeof LinearStreakLayout>
{
  private static readonly MAX_INSTANCES : number = 100;
  private static readonly RENDER_ORDER  : number = 30;
  
  constructor()
  {
    const base_geometry = new THREE.PlaneGeometry( 1, 1 );
    let atlas_texture: THREE.Texture | null = null;
    try { atlas_texture = Vfx3DGlobalAtlas.getTexture(); } catch ( _e ) {}

    const material = new THREE.ShaderMaterial( {
      uniforms: { 
        uTexture: { value: atlas_texture },
        uTime: { value: 0.0 }
      },
      vertexShader: `
        uniform float uTime;

        attribute vec4 aPosStartTime;
        attribute vec4 aVelDur;
        attribute vec4 aSizeStretch;
        attribute vec3 aColor;
        attribute vec4 aUvBounds;

        varying vec2 vUv;
        varying vec3 vColor;

        void main() {
          vUv = mix( aUvBounds.xy, aUvBounds.zw, uv );
          vColor = aColor;
          
          float start_time = aPosStartTime.w;
          float duration = aVelDur.w;
          float age = max( 0.0, uTime - start_time );
          float progress = clamp( age / duration, 0.0, 1.0 );
          
          float shrink_start = aSizeStretch.w;
          float scale_multiplier = 1.0;
          if ( progress > shrink_start ) {
            scale_multiplier = 1.0 - clamp( ( progress - shrink_start ) / max( 1.0 - shrink_start, 0.001 ), 0.0, 1.0 );
          }
          
          vec3 computed_pos = aPosStartTime.xyz + aVelDur.xyz * age;
          
          float speed = length( aVelDur.xyz );
          float stretch = ( aSizeStretch.y + speed * aSizeStretch.z ) * scale_multiplier;
          float width = aSizeStretch.x * scale_multiplier;
          
          vec3 dir_y = speed > 0.0001 ? ( aVelDur.xyz / speed ) : vec3( 0.0, 1.0, 0.0 );
          vec3 to_camera = normalize( cameraPosition - computed_pos );
          vec3 dir_x = cross( dir_y, to_camera );
          float len_x = length( dir_x );
          if ( len_x < 0.0001 ) {
            vec3 temp_up = abs( dir_y.y ) < 0.99 ? vec3( 0.0, 1.0, 0.0 ) : vec3( 1.0, 0.0, 0.0 );
            dir_x = cross( dir_y, temp_up );
            len_x = length( dir_x );
          }
          dir_x = dir_x / len_x;
          vec3 dir_z = cross( dir_x, dir_y );
          mat3 basis = mat3( dir_x, dir_y, dir_z );
          
          vec3 local_pos = vec3( position.x * width, position.y * stretch, 0.0 );
          vec3 world_pos = computed_pos + basis * local_pos;
          
          vec4 mvPosition = modelViewMatrix * vec4( world_pos, 1.0 );
          gl_Position = projectionMatrix * mvPosition;
        }
      `,
      fragmentShader: `
        uniform sampler2D uTexture;
        varying vec2 vUv;
        varying vec3 vColor;
        void main() {
          vec4 tex = texture2D( uTexture, vUv );
          gl_FragColor = vec4( tex.rgb * vColor * tex.a, tex.a );
        }
      `,
      transparent: true,
      depthWrite: false,
      depthTest: true,
      blending: THREE.AdditiveBlending,
      side: THREE.DoubleSide,
    } );

    super( { max_instances: VfxLinearStreakBatch.MAX_INSTANCES, layout: LinearStreakLayout }, base_geometry, material );
    this.instanced_mesh.renderOrder = VfxLinearStreakBatch.RENDER_ORDER;
  }

  public spawnParticle(
    res_id: ResourceID,
    start_pos: THREE.Vector3,
    velocity: THREE.Vector3,
    size: THREE.Vector2,
    color: THREE.Color,
    stretch_factor: number,
    shrink_start: number,
    start_time: number,
    duration: number
  ): number
  {
    const idx = this.allocateInstanceIndex( start_time, duration );
    if ( idx === -1 )
      return -1;

    const mat = this.material as THREE.ShaderMaterial;
    if ( !mat.uniforms.uTexture.value )
    {
      try { mat.uniforms.uTexture.value = Vfx3DGlobalAtlas.getTexture(); } catch ( _e ) {}
    }

    const bounds = Vfx3DGlobalAtlas.getUVRect( res_id );

    const pos_start_attr    = this.geometry.getAttribute( "aPosStartTime" ).array as Float32Array;
    const vel_dur_attr      = this.geometry.getAttribute( "aVelDur" ).array as Float32Array;
    const size_stretch_attr = this.geometry.getAttribute( "aSizeStretch" ).array as Float32Array;
    const color_attr        = this.geometry.getAttribute( "aColor" ).array as Float32Array;
    const uv_bounds_attr    = this.geometry.getAttribute( "aUvBounds" ).array as Float32Array;

    const o4 = idx * 4;
    const o3 = idx * 3;

    pos_start_attr[ o4 ]     = start_pos.x;
    pos_start_attr[ o4 + 1 ] = start_pos.y;
    pos_start_attr[ o4 + 2 ] = start_pos.z;
    pos_start_attr[ o4 + 3 ] = start_time;

    vel_dur_attr[ o4 ]     = velocity.x;
    vel_dur_attr[ o4 + 1 ] = velocity.y;
    vel_dur_attr[ o4 + 2 ] = velocity.z;
    vel_dur_attr[ o4 + 3 ] = duration;

    size_stretch_attr[ o4 ]     = size.x;
    size_stretch_attr[ o4 + 1 ] = size.y;
    size_stretch_attr[ o4 + 2 ] = stretch_factor;
    size_stretch_attr[ o4 + 3 ] = shrink_start;

    color_attr[ o3 ]     = color.r;
    color_attr[ o3 + 1 ] = color.g;
    color_attr[ o3 + 2 ] = color.b;

    uv_bounds_attr[ o4 ]     = bounds.min_x;
    uv_bounds_attr[ o4 + 1 ] = bounds.min_y;
    uv_bounds_attr[ o4 + 2 ] = bounds.max_x;
    uv_bounds_attr[ o4 + 3 ] = bounds.max_y;

    this.geometry.getAttribute( "aPosStartTime" ).needsUpdate = true;
    this.geometry.getAttribute( "aVelDur" ).needsUpdate = true;
    this.geometry.getAttribute( "aSizeStretch" ).needsUpdate = true;
    this.geometry.getAttribute( "aColor" ).needsUpdate = true;
    this.geometry.getAttribute( "aUvBounds" ).needsUpdate = true;

    return this.getStableId( idx );
  }

  protected override onUpdate( _dt: number, now: number ): void
  {
    const mat = this.material as THREE.ShaderMaterial;
    if ( mat.uniforms.uTime )
    {
      mat.uniforms.uTime.value = now;
    }
  }

  protected override updateInstance( _progress: number, _now: number, _idx: number ): boolean
  {
    return true;
  }
}
